import { setFollowUser } from "../map/mapSlice";
import {
  type SliceProps,
  setInternalCurrentLocation,
  setLocationQuality,
} from "./locationSlice";

import { Location } from "@/libs/models";
import store from "../../app/store";
import { selectBuildingByFloorId } from "../cartography/actions";
import { LocationStatusTypes } from "../utils/types";
import { processLocationQuality, rotation } from "./libs/location";

// Transforms location so rotation always turns to the right side
// Dispatches it to the store
export const handleLocationUpdate = async (
  position: SliceProps["currentLocation"],
) => {
  if (!position) return;

  const currentLocation = store.getState().location.internalCurrentLocation;

  // Restore if LocationQuality is needed again
  const thresholds = store.getState().config.internal.locationQualityThresholds;

  if (store.getState().config.feature_flags.ar_integration) {
    const newQuality = currentLocation
      ? processLocationQuality(
          store.getState().location.locationQuality,
          currentLocation,
          thresholds,
        )
      : null;
    newQuality && store.dispatch(setLocationQuality(newQuality));
  }

  const newLocation = position;

  // Sanitize location information
  newLocation.buildingId = Number(newLocation.buildingId);
  newLocation.floorId = Number(newLocation.floorId);
  newLocation.ingestionTimestamp = Date.now();
  const currentBuilding =
    store.getState().cartography.buildings[newLocation.buildingId];

  // iOS ExternalLocation does not specify cartesianCoordinates,
  // so convert the polar coordinates obtained to make navigation posible.
  if (
    (newLocation.x === 0 && newLocation.y === 0) ||
    (newLocation.x === undefined && newLocation.y === undefined)
  ) {
    const obtainedCartesianCoords =
      currentBuilding?.value.coordinateConverterFlatEarth.toCartesian({
        lat: newLocation.latitude,
        lng: newLocation.longitude,
      });
    if (obtainedCartesianCoords) {
      newLocation.x = obtainedCartesianCoords.x ?? 0;
      newLocation.y = obtainedCartesianCoords.y ?? 0;
    }
  }

  // force to reconvert cartesian to lat long, since sdks coordinate converters have low precision
  const convertedCoordinates =
    currentBuilding?.value.coordinateConverterFlatEarth.toCoordinate({
      x: newLocation.x,
      y: newLocation.y,
    });
  if (convertedCoordinates) {
    newLocation.latitude = convertedCoordinates.lat ?? 0;
    newLocation.longitude = convertedCoordinates.lng ?? 0;
  }

  // If we are OOB set a fixed accuracy radius
  if (newLocation.status === LocationStatusTypes.OOB) {
    newLocation.accuracy = 200;
  }

  if (!hasConsistentLocationData(newLocation)) {
    console.error("New location has inconsitent data");
    return;
  }
  if (currentLocation) {
    // If we had a previous location, smooth rotation if necessary
    if (newLocation.hasBearing) {
      const angle = rotation(currentLocation.bearing, newLocation.bearing);
      newLocation.bearing = currentLocation.bearing + angle;
    } else {
      newLocation.bearing = currentLocation.bearing;
    }

    // Force follow user if location floor changes or the location is new
    if (currentLocation?.floorId !== newLocation.floorId) {
      store.dispatch(setFollowUser(true));
    }
  } else {
    // Force follow user if location is new
    store.dispatch(setFollowUser(true));
  }

  store.dispatch(setInternalCurrentLocation(new Location({ ...newLocation })));
};

/**
 * Evaluates whether the provided location data is consistent by
 * verifying that the given `floorId` belongs to the specified `buildingId`.
 *
 * @param currentLocation - The current location object
 * @returns `true` if the data is consistent otherwise it will return `false`
 */
const hasConsistentLocationData = (currentLocation: Location): boolean => {
  if (currentLocation.buildingId === -1 && currentLocation.floorId === -1)
    return true;

  const building = selectBuildingByFloorId(currentLocation.floorId);
  return building?.id === currentLocation.buildingId;
};
